import React, { memo } from 'react';
import styled from 'styled-components';

import { v4 as uuidv4 } from 'uuid';
import { Box, IconButton, Stack, Switch } from '@mui/material';
import { Command, tabActions } from '@store/slices';
import { useDispatch } from 'react-redux';
import { AddOutlined, DeleteOutlineOutlined, InfoOutlined } from '@mui/icons-material';
import { Button } from '@common/Button';
import { Label } from '@common/Label';
import { useTheme } from '../../../../styles/theme';
import { NodeField, NodeRequestParameter } from '@components/Flow/Nodes';
import { BasicInput } from '@common/Expression/Basic';
import { Tooltip } from '@common/Tooltip';
import { SubText } from '@common/SubText/SubText';
import { ErrorMessage } from '@common/Autocomplete';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;

  & .MuiInputAdornment-root {
    margin-right: 10px;
  }
`;

const Action = styled.div`
  display: flex;
  align-items: center;

  svg {
    font-size: 2ch;
  }
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const RequestParameters: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { state } = command;
  const { id, label, description } = field;

  const requestParameters = state[id] as NodeRequestParameter<string>[];

  const addParameter = () => {
    const parameterType = field.id === 'query_parameters' ? 'query' : 'body';
    const value = { id: uuidv4(), name: '', value: '', required: true, type: parameterType };

    const input = {
      nodeId: command.node.id,
      fieldId: id,
      value: value,
    };

    dispatch(tabActions.nodeInputArrayPush(input));
    dispatch(tabActions.setSaved(false));
  };

  const updateField = (nestedFieldId: string, fieldName: string, newValue: string | boolean) => {
    const input = {
      nodeId: command.node.id,
      fieldId: id,
      nestedFieldId,
      fieldName,
      value: newValue,
    };

    dispatch(tabActions.nodeInputArrayUpdate(input));
    dispatch(tabActions.setSaved(false));
  };

  const deleteField = (nestedFieldId: string) => {
    dispatch(
      tabActions.nodeInputArrayFilter({
        nodeId: command.node.id,
        fieldId: id,
        nestedFieldId,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const isDuplicateName = (name: string, type: string): boolean => {
    let count = 0;

    for (const param of requestParameters) {
      if (param.name && param.name === name && param.type === type) {
        count++;
        if (count > 1) {
          return true;
        }
      }
    }

    return false;
  };

  if (!field || !command) return null;

  return (
    <Container>
      <Stack direction={'row'} marginTop={1} justifyContent={'space-between'}>
        <Label
          title={label}
          tooltip={description}
          onClick={requestParameters?.length !== 0 && requestParameters ? addParameter : undefined}
        />
        {requestParameters?.length !== 0 && requestParameters && (
          <Stack alignItems='center' spacing={0.75} direction='row'>
            <Tooltip
              sx={{ maxWidth: '300px' }}
              placement='top'
              arrow
              title={`The parameters that are required for the request. Please check each parameter for the required field.\nNote: The parameters by default are required.`}
            >
              <InfoOutlined sx={{ fontSize: '16px', color: theme.text.subtext }} />
            </Tooltip>
            <SubText>Required</SubText>
          </Stack>
        )}
      </Stack>
      <Stack spacing={1.25}>
        {(requestParameters || [])?.map(({ id, name, required, type = 'unknown' }) => (
          <Parameter
            key={id}
            id={id}
            name={name}
            required={required || required === undefined}
            nodeId={command.node.id}
            fieldId={field.id}
            updateField={updateField}
            deleteField={deleteField}
            type={type}
            isDuplicateName={isDuplicateName(name, type)}
          />
        ))}
        {(requestParameters?.length === 0 || !requestParameters) && (
          <Button color='primary' fullWidth onClick={addParameter}>
            <AddOutlined sx={{ fontSize: '14px', color: `${theme.text.standard} !important` }} />
          </Button>
        )}
      </Stack>
    </Container>
  );
};

interface ParameterProps {
  id: string;
  name: string;
  required: boolean;
  nodeId: string;
  fieldId: string;
  updateField: (nestedFieldId: string, fieldName: string, newValue: string | boolean) => void;
  deleteField: (nestedFieldId: string) => void;
  isDuplicateName: boolean;
  type: string;
}

const ParameterContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 8px;

  .input-wrapper {
    max-width: 100%;
    overflow: hidden;
  }
`;

const Parameter = memo(
  ({ id, name, required, nodeId, fieldId, updateField, deleteField, isDuplicateName, type }: ParameterProps) => {
    const theme = useTheme();
    const checkboxDisabled = !name || name === '';

    return (
      <Stack spacing={0.5}>
        <ParameterContainer>
          <div className='input-wrapper'>
            <BasicInput
              key={`${nodeId}-${fieldId}-name`}
              keyProp={`${nodeId}-${fieldId}`}
              value={name}
              setValue={(newValue: string) => updateField(id, 'name', newValue)}
              placeholder='param'
              minRows={1}
              maxRows={1}
              wrapping={false}
            />
          </div>
          <Action>
            <IconButton
              sx={{ border: `1px solid ${theme.line.light}`, height: '42px', width: '42px' }}
              onClick={() => deleteField(id)}
            >
              <DeleteOutlineOutlined sx={{ fontSize: '1.4ch !important', color: theme.text.subtext }} />
            </IconButton>
          </Action>
          <Action>
            <Box
              sx={{
                border: `1px solid ${theme.line.light}`,
                borderRadius: '6px',
                padding: '8px 4px',
              }}
            >
              <Switch
                size='small'
                disabled={checkboxDisabled || isDuplicateName}
                checked={required && !checkboxDisabled}
                onChange={(e) => updateField(id, 'required', e?.target?.checked)}
              />
            </Box>
          </Action>
        </ParameterContainer>
        {isDuplicateName && <ErrorMessage>A {type} parameter already uses this name.</ErrorMessage>}
      </Stack>
    );
  }
);

Parameter.displayName = 'Parameter';
